import { useRef, useEffect, useState, useContext } from "react";
import ComPDFKitViewer from "@compdfkit_pdf_sdk/webviewer";
import "./index.css";
import SpaceBetween from "@cloudscape-design/components/space-between";
import { Card, Row, Col, Button, Flex } from "antd";
import { getFileUrl } from "../../config";
import TemplateDesignerTagsList from "../digitalforms/digitalform-tags-list";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetDigitalFormQuery,
  useUpdateDigitalFormCOMPDFAnnotationMutation,
} from "../../redux/api/digitalform/digitalforms";
import { skipToken } from "@reduxjs/toolkit/query";
import { isArray } from "lodash";
import {
  convertStringToCustomDataObj,
  convertCustomDataObjToString,
} from "../../utils/compdf-annote-helper.js";
import COMPDFAnnoteFormatter from "../../components/digitalforms/compdf-annot-formatter";
import { ZoomInOutlined, ZoomOutOutlined } from "@ant-design/icons";
import { SpinnerContext } from "../../context/spinner/spinner";
import { ToastContext } from "../../context/toast/incredable-toast";

export const convertXMLStringBlob = (xml) => {
  return new Blob([xml], {
    type: "application/xfdf",
  });
};

export default function COMPDFTemplateEditor() {
  const { digitalformId } = useParams();
  const containerRef = useRef(null);
  const navigate = useNavigate();

  const [instance, setInstance] = useState(undefined);
  const [selectedAnnots, setSelectedAnnots] = useState();
  const [documentLoaded, setDocumentLoaded] = useState(false);
  const [annoteImported, setAnnoteImported] = useState(false);
  const spinnerContext = useContext(SpinnerContext);
  const toastContext = useContext(ToastContext);

  const { data: digitalform } = useGetDigitalFormQuery(
    digitalformId ?? skipToken
  );

  const [updateDigitalFormCOMPDFAnnotation] =
    useUpdateDigitalFormCOMPDFAnnotationMutation();

  useEffect(() => {
    if (containerRef.current != null && !!digitalform?.flattenedTemplate?.key)
      ComPDFKitViewer.init(
        {
          path: "/",
          license: "Njg0YmEwYjM5OTZkNw==",
        },
        containerRef.current
      )
        .then((instance) => {
          setInstance(instance);
          const { UI } = instance;
          UI.textPopup.update([]);
          UI.disableElements(["header"]);
          instance.UI.closeElement("leftPanel");

          if ("localhost" === window.location.hostname)
            instance.UI.loadDocument("./example/developer_guide_web.pdf");
          else
            instance.UI.loadDocument(
              encodeURI(getFileUrl(digitalform?.flattenedTemplate?.key)),
              {
                withCredentials: true,
              }
            );
          let docViewer = instance.docViewer;
          docViewer.setToolMode("form");
          docViewer.addEvent("documentloaded", async () => {
            instance.UI.closeElement("rightPanel");
            docViewer.setToolMode("form");
            const pageWidth = instance.docViewer.getPageWidth(1);
            const viewerContainer = document.getElementById("compdf");
            const viewerWidth = viewerContainer.clientWidth;
            docViewer.webViewerScaleChanged(viewerWidth / pageWidth);

            setDocumentLoaded(true);
          });

          docViewer.addEvent("annotationSelected", (annote) => {
            const annotationManager = docViewer.getAnnotationManager();
            const annotationsList = annotationManager.getAnnotationsList();
            const foundAnnote = annotationsList?.find(
              (a) => a?.name === annote?.name
            );

            if (!!foundAnnote) setSelectedAnnots(foundAnnote);
          });
        })
        .catch((error) => {
          console.log(error);
        });
  }, [digitalform]);

  const addFreeTextAnnotation = async (point, field) => {
    if (!instance) return;

    const { docViewer } = instance;

    const page = docViewer.getSelectedPage(point, point);
    const width = field.type === "bool" ? 20 : 180;
    const height = field.type === "bool" ? 40 : 50;

    if (!!point.x && page.first == null) {
      console.warn("invalid page location");
      return; //don't add field to an invalid page location
    }

    const page_idx =
      page.first !== null ? page.first : docViewer.getCurrentPage();

    //  const page_info = doc.getPageInfo(page_idx); getPageWidth(pageNumber)
    const page_point = docViewer.windowToPage(point, page_idx);

    const pageIndex = page_idx;

    const X = page_point.x + 8;
    const Y = page_point.y - 12;
    let customData = { ...field };

    if (
      field.isList &&
      !!docViewer.getAnnotationManager().getAnnotationsList()
    ) {
      try {
        const flattened = Object.values(
          docViewer.getAnnotationManager().getAnnotationsList()
        ).flat();
        const index = flattened?.filter((annot) => {
          const annoteCustomData = convertStringToCustomDataObj(
            annot.fieldName
          );
          return annoteCustomData?.key + "" === field.key.toString();
        }).length;
        customData = { ...customData, index: index };
      } catch (error) {
        console.log(error);
      }
    }
    docViewer.addAnnotations({
      type: "textfield",
      rect: {
        left: X,
        top: Y,
        right: X + width,
        bottom: Y + height / 2,
      },
      fieldName: convertCustomDataObjToString(customData),
      isHidden: 0,
      contents: field?.label,
      backgroundColor: "#93B9FD",
      fillColor: "#93B9FD",
      color: "#000000",
      fontName: "Times New Roman",
      fontSize: 14,
      textAlignment: "left",
      isMultiLine: false,
      pageIndex: pageIndex - 1,
    });
  };

  const dragStart = (e) => {
    e.currentTarget.style.opacity = "0.5";
    const copy = e.currentTarget.cloneNode(true);
    copy.id = "form-build-drag-image-copy";
    copy.style.width = "250px";
    document.body.appendChild(copy);
    e.dataTransfer.setDragImage(copy, 10, 25);
    e.dataTransfer.setData("text", "");
  };

  const dragEnd = (e, field) => {
    const { docViewer } = instance;
    const scrollElement = docViewer.getScrollViewElement();
    const scrollLeft = scrollElement.scrollLeft || 0;
    const scrollTop = scrollElement.scrollTop || 0;

    addFreeTextAnnotation(
      { x: e.pageX + scrollLeft, y: e.pageY + scrollTop },
      field
    );

    e.currentTarget.style.opacity = "1";
    document.body.removeChild(
      document.getElementById("form-build-drag-image-copy")
    );
    e.preventDefault();
  };

  useEffect(() => {
    if (
      !documentLoaded ||
      annoteImported ||
      !instance ||
      !digitalform ||
      !digitalform?.annotationsJson
    )
      return;
    const docViewer = instance?.docViewer;
    const annotes = JSON.parse(digitalform?.annotationsJson);
    if (!!annotes && isArray(annotes)) {
      annotes?.forEach((annote) => {
        delete annote?.date;
        console.log({ annote });
        docViewer.addAnnotations(annote);
      });
    }
    setAnnoteImported(true);
  }, [documentLoaded, annoteImported, digitalform, instance]);

  return (
    <Card
      title={
        <Flex justify={"space-between"}>
          <span style={{ fontSize: "18px", marginTop: "4px" }}>
            {" "}
            {digitalform?.name ?? "Template Editor"}
          </span>
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              icon={<ZoomInOutlined />}
              onClick={() => {
                const { docViewer } = instance;
                docViewer.zoomIn();
              }}
            />
            <Button
              icon={<ZoomOutOutlined />}
              onClick={() => {
                const { docViewer } = instance;
                docViewer.zoomOut();
              }}
            />
          </SpaceBetween>
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              onClick={async () => {
                navigate(-1);
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={async () => {
                if (!instance) return;
                spinnerContext?.showSpinner();
                try {
                  const { docViewer } = instance;
                  const annotationManagerAnnotationsList = docViewer
                    .getAnnotationManager()
                    .getAnnotationsList();
                  const annotationsList = annotationManagerAnnotationsList?.map(
                    (annote) => annote?.annotation
                  );
                  const annotationJSON = JSON.stringify(annotationsList);
                  if (!!annotationJSON && !!digitalform?.id) {
                    await updateDigitalFormCOMPDFAnnotation({
                      id: digitalform?.id,
                      annotationJSON: annotationJSON,
                    }).unwrap();
                    toastContext?.openInfoNotification({
                      message: `Template saved successfully`,
                      placement: "topRight",
                    });
                    navigate(-1);
                  }
                } catch (error) {
                  toastContext?.openErrorNotification({
                    message:
                      error?.data?.errorMessage || "Failed to update template",
                    placement: "topRight",
                  });
                }
                spinnerContext?.hidespinner();
              }}
            >
              Save
            </Button>
          </SpaceBetween>
        </Flex>
      }
    >
      <Row gutter={24}>
        <Col span={8}>
          {!selectedAnnots ? (
            <TemplateDesignerTagsList
              onDragEnd={dragEnd}
              onDragStart={dragStart}
            />
          ) : (
            <COMPDFAnnoteFormatter
              annote={selectedAnnots}
              docViewer={instance?.docViewer}
              onCancel={() => {
                setSelectedAnnots();
              }}
            />
          )}
        </Col>
        <Col span={16}>
          <div
            id="compdf"
            ref={containerRef}
            style={{ height: "100vh", overflow: "hidden" }}
          />
        </Col>
      </Row>
    </Card>
  );
}
